import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CircularProgress from "@mui/material/CircularProgress";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import LaunchIcon from "@mui/icons-material/Launch";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import GavelIcon from "@mui/icons-material/Gavel";
import BlockIcon from "@mui/icons-material/Block";
import PaymentIcon from "@mui/icons-material/Payment";
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import StorefrontIcon from "@mui/icons-material/Storefront";
import SettingsIcon from "@mui/icons-material/Settings";
import Header from "../../Components/Header/Header";
import Sidebar from "../../Components/Sidebar/Sidebar";
import { getStripeBalance, getStripeLoginLink, getEarningsSummary } from "../../api/payments";
import { getRefferalDetails } from "../../api/affliate";
import { getToken, getUserId } from "../../api";
import "../AdventureShared.css";
import "./WalletPage.css";

export default function WalletPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "overview"; // 'overview' | 'marketplace' | 'affiliate' | 'payouts'

  const [isLoading, setIsLoading] = useState(true);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [copiedType, setCopiedType] = useState("");

  const [stripeBalance, setStripeBalance] = useState({
    availableAmount: 0,
    pendingAmount: 0,
    currency: "USD"
  });

  const [earningsSummary, setEarningsSummary] = useState({
    totalGrossEarnings: 0,
    totalNetEarnings: 0,
    lockedInEscrow: 0,
    paidOutToStripe: 0,
    ledger: []
  });

  const [referralData, setReferralData] = useState(null);

  // Redux user details for affiliate links
  const userDetails = useSelector((state) => state.userDetails.userDetails) || {};
  const affiliateCode = userDetails.affiliateCode || "N/A";
  const affiliateLink = userDetails.referralUrl || "";

  // Pagination states for marketplace ledger
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const setTab = (tabName) => {
    setSearchParams({ tab: tabName });
  };

  useEffect(() => {
    loadAllFinancialData();
  }, []);

  const loadAllFinancialData = async () => {
    setIsLoading(true);
    const token = getToken();
    const userId = getUserId();

    try {
      const promises = [
        getStripeBalance().catch(err => {
          console.warn("Could not fetch Stripe balance:", err);
          return null;
        }),
        getEarningsSummary().catch(err => {
          console.warn("Could not fetch earnings summary:", err);
          return null;
        })
      ];

      if (userId && token) {
        promises.push(
          getRefferalDetails(userId, token).catch(err => {
            console.warn("Could not fetch affiliate details:", err);
            return null;
          })
        );
      }

      const [balanceRes, summaryRes, affiliateRes] = await Promise.all(promises);

      if (balanceRes && balanceRes.success && balanceRes.data) {
        setStripeBalance(balanceRes.data);
      }
      
      if (summaryRes && summaryRes.success && summaryRes.data) {
        setEarningsSummary(summaryRes.data);
      }

      if (affiliateRes) {
        setReferralData(affiliateRes);
      }
    } catch (err) {
      console.error("Error loading wallet details:", err);
      toast.error("Failed to retrieve real-time wallet details.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLaunchDashboard = async () => {
    setIsRedirecting(true);
    try {
      const response = await getStripeLoginLink();
      if (response && response.success && response.data) {
        window.open(response.data, "_blank", "noopener,noreferrer");
      } else {
        toast.error(response?.message || "Failed to generate Stripe dashboard redirection link.");
      }
    } catch (err) {
      console.error("Error generating dashboard link", err);
      toast.error("Could not redirect to Stripe payouts dashboard.");
    } finally {
      setIsRedirecting(false);
    }
  };

  const handleCopy = async (text, type) => {
    if (!text) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopiedType(type);
      toast.success(`${type === 'link' ? 'Referral Link' : 'Affiliate Code'} copied to clipboard!`);
      setTimeout(() => setCopiedType(""), 2200);
    } catch (err) {
      console.error("Copy failed", err);
    }
  };

  // Status badge helper
  const getStatusConfig = (status) => {
    switch (status) {
      case "Released":
        return {
          label: "Released",
          className: "status-released",
          icon: <CheckCircleIcon className="ledger-status-icon" />
        };
      case "Held in Escrow":
        return {
          label: "Held in Escrow",
          className: "status-escrow",
          icon: <HourglassEmptyIcon className="ledger-status-icon" />
        };
      case "Disputed":
        return {
          label: "Disputed",
          className: "status-disputed",
          icon: <GavelIcon className="ledger-status-icon" />
        };
      case "Refunded":
        return {
          label: "Refunded",
          className: "status-refunded",
          icon: <BlockIcon className="ledger-status-icon" />
        };
      default:
        return {
          label: status || "Pending",
          className: "status-unpaid",
          icon: <PaymentIcon className="ledger-status-icon" />
        };
    }
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: stripeBalance.currency || "USD"
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const getUserInitials = (firstName, lastName) => {
    const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : "";
    const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : "";
    return firstInitial + lastInitial || "U";
  };

  // Financial calculations
  const marketplaceNet = earningsSummary.totalNetEarnings || 0;
  const marketplaceEscrow = earningsSummary.lockedInEscrow || 0;
  const affiliateTotal = Number(referralData?.data?.totalEarning || 0);
  const affiliateReferralsCount = Number(referralData?.data?.totalReferral || 0);
  const combinedTotalEarnings = marketplaceNet + affiliateTotal;

  // Compute pagination for ledger
  const ledger = earningsSummary.ledger || [];
  const totalPages = Math.max(1, Math.ceil(ledger.length / itemsPerPage));
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentLedgerItems = ledger.slice(indexOfFirstItem, indexOfLastItem);

  // Compute trend chart
  const getChartData = () => {
    const monthlyData = {};
    for (let i = 5; i >= 0; i--) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      const monthName = d.toLocaleString("en-US", { month: "short" });
      monthlyData[monthName] = 0;
    }
    ledger.forEach((item) => {
      if (item.escrowStatus === "Released" && item.completedAt) {
        const monthName = new Date(item.completedAt).toLocaleString("en-US", { month: "short" });
        if (monthlyData[monthName] !== undefined) {
          monthlyData[monthName] += item.netPayout;
        }
      }
    });
    return Object.entries(monthlyData).map(([month, amount]) => ({
      month,
      amount
    }));
  };

  const chartPoints = getChartData();
  const maxAmount = Math.max(...chartPoints.map(p => p.amount), 10);
  const width = 500;
  const height = 150;
  const padding = 20;
  const chartWidth = width - padding * 2;
  const chartHeight = height - padding * 2;
  const coords = chartPoints.map((p, index) => {
    const x = padding + (index / (chartPoints.length - 1)) * chartWidth;
    const y = padding + chartHeight - (p.amount / maxAmount) * chartHeight;
    return { x, y, ...p };
  });
  const linePath = coords.map((c, idx) => (idx === 0 ? `M ${c.x} ${c.y}` : `L ${c.x} ${c.y}`)).join(" ");
  const areaPath = coords.length > 0 
    ? `${linePath} L ${coords[coords.length - 1].x} ${height - padding} L ${coords[0].x} ${height - padding} Z` 
    : "";

  return (
    <div className="BookingsPage">
      <div className="adventurePageContainer">
        <div className="headerContain">
          <Header />
        </div>
        <div className="adventurePageContainer2">
          <div className="sidebarContain">
            <Sidebar />
          </div>
          <main className="adv-page-main">
            {/* Standard Page Header */}
            <div className="adv-page-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                <div>
                  <h1 className="adv-page-title">
                    Earnings &amp; <span className="adv-page-title-accent">Wallet</span>
                  </h1>
                  <p className="adv-page-sub">
                    Unified balance and payouts for marketplace services, events, and affiliate commissions.
                  </p>
                </div>

                <div className="wallet-header-actions">
                  <button
                    className="payout-btn-primary"
                    onClick={handleLaunchDashboard}
                    disabled={isRedirecting || isLoading}
                  >
                    {isRedirecting ? (
                      <>
                        <CircularProgress size={16} color="inherit" />
                        <span style={{ marginLeft: "8px" }}>Connecting...</span>
                      </>
                    ) : (
                      <>
                        <LaunchIcon style={{ fontSize: "18px", marginRight: "6px" }} />
                        Stripe Dashboard
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Financial Navigation Tabs */}
            <div className="wallet-nav-tabs">
              <button
                className={`wallet-nav-tab ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => setTab('overview')}
              >
                💼 All Earnings (Overview)
              </button>
              <button
                className={`wallet-nav-tab ${activeTab === 'marketplace' ? 'active' : ''}`}
                onClick={() => setTab('marketplace')}
              >
                🛍️ Marketplace &amp; Services
              </button>
              <button
                className={`wallet-nav-tab ${activeTab === 'affiliate' ? 'active' : ''}`}
                onClick={() => setTab('affiliate')}
              >
                🤝 Affiliate &amp; Referrals
              </button>
              <button
                className={`wallet-nav-tab ${activeTab === 'payouts' ? 'active' : ''}`}
                onClick={() => navigate('/facilitate/settings/payouts')}
              >
                ⚙️ Payout Settings
              </button>
            </div>

            <div className="wallet-settings-container animate-fade-in">
              {isLoading ? (
                <div className="wallet-loader-container">
                  <CircularProgress size={45} className="loader-spinner" />
                  <p className="loader-text">Loading financial statements...</p>
                </div>
              ) : (
                <>
                  {/* ────────────────────────────────────────────────
                      TAB 1: ALL EARNINGS OVERVIEW
                      ──────────────────────────────────────────────── */}
                  {activeTab === 'overview' && (
                    <>
                      {/* Financial KPI Cards Row (4 Unified Cards) */}
                      <div className="wallet-cards-grid wallet-cards-grid--4">
                        {/* 1. Combined Total Earnings */}
                        <div className="kpi-card glassmorphism card-total">
                          <div className="kpi-card-header">
                            <div className="kpi-card-icon-wrapper icon-total">
                              <TrendingUpIcon />
                            </div>
                            <span className="kpi-card-label">Total Combined Earnings</span>
                          </div>
                          <div className="kpi-card-value">
                            {formatMoney(combinedTotalEarnings)}
                          </div>
                          <div className="wallet-card-tag-row">
                            <span className="wallet-badge-pill">Services: {formatMoney(marketplaceNet)}</span>
                            <span className="wallet-badge-pill">Affiliate: {formatMoney(affiliateTotal)}</span>
                          </div>
                        </div>

                        {/* 2. Stripe Available Balance */}
                        <div className="kpi-card glassmorphism card-stripe">
                          <div className="kpi-card-header">
                            <div className="kpi-card-icon-wrapper icon-stripe">
                              <AccountBalanceWalletIcon />
                            </div>
                            <span className="kpi-card-label">Stripe Available Balance</span>
                          </div>
                          <div className="kpi-card-value">
                            {formatMoney(stripeBalance.availableAmount)}
                          </div>
                          <div className="stripe-balance-breakdown">
                            <span>Ready to Disburse</span>
                            <span className="stripe-pending">Pending: {formatMoney(stripeBalance.pendingAmount)}</span>
                          </div>
                        </div>

                        {/* 3. Held in Escrow */}
                        <div className="kpi-card glassmorphism card-escrow">
                          <div className="kpi-card-header">
                            <div className="kpi-card-icon-wrapper icon-escrow">
                              <HourglassEmptyIcon />
                            </div>
                            <span className="kpi-card-label">Held in Escrow</span>
                          </div>
                          <div className="kpi-card-value">
                            {formatMoney(marketplaceEscrow)}
                          </div>
                          <p className="kpi-card-desc">
                            In-progress service contracts waiting for customer sign-off
                          </p>
                        </div>

                        {/* 4. Affiliate Referral Commissions */}
                        <div className="kpi-card glassmorphism card-affiliate">
                          <div className="kpi-card-header">
                            <div className="kpi-card-icon-wrapper icon-affiliate">
                              <PeopleAltIcon />
                            </div>
                            <span className="kpi-card-label">Affiliate Commissions</span>
                          </div>
                          <div className="kpi-card-value">
                            {formatMoney(affiliateTotal)}
                          </div>
                          <div className="wallet-card-tag-row">
                            <span className="wallet-badge-pill">{affiliateReferralsCount} Subscribers Referred</span>
                          </div>
                        </div>
                      </div>

                      {/* Earnings Trend SVG Chart */}
                      <div className="earnings-chart-container glassmorphism animate-fade-in">
                        <div className="chart-header">
                          <h2 className="chart-title">Earnings Performance (Released Payouts)</h2>
                          <span className="chart-subtitle">Last 6 Months Trend</span>
                        </div>
                        <div className="chart-body">
                          {ledger.length === 0 ? (
                            <div className="empty-chart-state">
                              <span>No transaction data available to plot trends.</span>
                            </div>
                          ) : (
                            <div className="svg-wrapper">
                              <svg viewBox={`0 0 ${width} ${height}`} className="earnings-svg">
                                <defs>
                                  <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#5AAF9A" stopOpacity="0.4" />
                                    <stop offset="100%" stopColor="#5AAF9A" stopOpacity="0.0" />
                                  </linearGradient>
                                  <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="0">
                                    <stop offset="0%" stopColor="#5AAF9A" />
                                    <stop offset="100%" stopColor="#6bc2ad" />
                                  </linearGradient>
                                </defs>
                                
                                {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
                                  const y = padding + chartHeight * ratio;
                                  return (
                                    <line 
                                      key={i} 
                                      x1={padding} 
                                      y1={y} 
                                      x2={width - padding} 
                                      y2={y} 
                                      stroke="rgba(255,255,255,0.05)" 
                                      strokeWidth="1" 
                                    />
                                  );
                                })}

                                {areaPath && <path d={areaPath} fill="url(#chartGrad)" />}
                                {linePath && (
                                  <path 
                                    d={linePath} 
                                    fill="none" 
                                    stroke="url(#lineGrad)" 
                                    strokeWidth="3" 
                                    strokeLinecap="round" 
                                  />
                                )}

                                {coords.map((c, i) => (
                                  <g key={i} className="chart-point">
                                    <circle cx={c.x} cy={c.y} r="4" fill="#5AAF9A" stroke="#0e1b1b" strokeWidth="2" />
                                    <text x={c.x} y={height - 5} textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="10">
                                      {c.month}
                                    </text>
                                  </g>
                                ))}
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Recent Transactions Ledger Table */}
                      <div className="ledger-section glassmorphism animate-fade-in">
                        <div className="ledger-header">
                          <h2 className="ledger-title">Recent Transactions &amp; Disbursements</h2>
                          <span className="ledger-count">{ledger.length} total entries</span>
                        </div>

                        {ledger.length === 0 ? (
                          <div className="empty-ledger-state">
                            <AccessTimeIcon className="empty-icon" />
                            <p>No transaction history found on your account.</p>
                          </div>
                        ) : (
                          <>
                            <div className="table-responsive">
                              <table className="ledger-table">
                                <thead>
                                  <tr>
                                    <th>Transaction Ref</th>
                                    <th>Source / Service</th>
                                    <th>Type</th>
                                    <th>Net Payout</th>
                                    <th>Status</th>
                                    <th>Date</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {currentLedgerItems.map((tx, idx) => {
                                    const statusCfg = getStatusConfig(tx.escrowStatus);
                                    return (
                                      <tr key={tx._id || idx} className="ledger-row">
                                        <td className="col-tx-id">
                                          <span>{tx.stripeTransferId || tx._id || `TXN-${idx}`}</span>
                                        </td>
                                        <td className="col-offering">
                                          <span className="offering-title">{tx.offeringTitle || "Service Booking"}</span>
                                        </td>
                                        <td>
                                          <span className="ledger-type-badge type-service">Marketplace</span>
                                        </td>
                                        <td className="col-amount">
                                          <span className="net-amount">{formatMoney(tx.netPayout)}</span>
                                        </td>
                                        <td className="col-status">
                                          <span className={`status-pill ${statusCfg.className}`}>
                                            {statusCfg.icon}
                                            {statusCfg.label}
                                          </span>
                                        </td>
                                        <td className="col-date">
                                          {formatDate(tx.completedAt || tx.createdAt)}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>

                            {totalPages > 1 && (
                              <div className="ledger-pagination">
                                <button
                                  className="pagination-btn"
                                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                  disabled={currentPage === 1}
                                >
                                  <KeyboardArrowLeftIcon />
                                </button>
                                <span className="pagination-info">
                                  Page {currentPage} of {totalPages}
                                </span>
                                <button
                                  className="pagination-btn"
                                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                  disabled={currentPage === totalPages}
                                >
                                  <KeyboardArrowRightIcon />
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </>
                  )}

                  {/* ────────────────────────────────────────────────
                      TAB 2: MARKETPLACE & SERVICES
                      ──────────────────────────────────────────────── */}
                  {activeTab === 'marketplace' && (
                    <div className="wallet-tab-content animate-fade-in">
                      <div className="wallet-cards-grid">
                        <div className="kpi-card glassmorphism card-total">
                          <div className="kpi-card-header">
                            <div className="kpi-card-icon-wrapper icon-total"><StorefrontIcon /></div>
                            <span className="kpi-card-label">Marketplace Net Earnings</span>
                          </div>
                          <div className="kpi-card-value">{formatMoney(marketplaceNet)}</div>
                          <p className="kpi-card-desc">Total released from completed client bookings</p>
                        </div>

                        <div className="kpi-card glassmorphism card-escrow">
                          <div className="kpi-card-header">
                            <div className="kpi-card-icon-wrapper icon-escrow"><HourglassEmptyIcon /></div>
                            <span className="kpi-card-label">Active Escrow Protection</span>
                          </div>
                          <div className="kpi-card-value">{formatMoney(marketplaceEscrow)}</div>
                          <p className="kpi-card-desc">Protected by Amalgamate escrow until client approves delivery</p>
                        </div>
                      </div>

                      <div className="affiliate-payout-note" style={{ marginTop: '20px' }}>
                        <strong>Direct Stripe Escrow Release:</strong> When customers confirm completed services or events, funds are immediately moved from Escrow to your Stripe Express balance and disbursed to your linked bank account.
                      </div>

                      {/* Marketplace Ledger Table */}
                      <div className="ledger-section glassmorphism animate-fade-in" style={{ marginTop: '24px' }}>
                        <div className="ledger-header">
                          <h2 className="ledger-title">Marketplace Booking Ledger</h2>
                          <span className="ledger-count">{ledger.length} bookings</span>
                        </div>
                        {ledger.length === 0 ? (
                          <div className="empty-ledger-state">
                            <StorefrontIcon className="empty-icon" />
                            <p>No marketplace bookings or gig payouts recorded yet.</p>
                          </div>
                        ) : (
                          <div className="table-responsive">
                            <table className="ledger-table">
                              <thead>
                                <tr>
                                  <th>Booking Title</th>
                                  <th>Gross Fee</th>
                                  <th>Net Earnings</th>
                                  <th>Escrow Status</th>
                                  <th>Date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {ledger.map((tx, idx) => {
                                  const statusCfg = getStatusConfig(tx.escrowStatus);
                                  return (
                                    <tr key={tx._id || idx} className="ledger-row">
                                      <td className="col-offering"><strong>{tx.offeringTitle || "Service Contract"}</strong></td>
                                      <td>{formatMoney(tx.grossAmount || tx.totalAmount || tx.netPayout)}</td>
                                      <td><strong style={{ color: '#48d1cc' }}>{formatMoney(tx.netPayout)}</strong></td>
                                      <td>
                                        <span className={`status-pill ${statusCfg.className}`}>
                                          {statusCfg.icon} {statusCfg.label}
                                        </span>
                                      </td>
                                      <td>{formatDate(tx.completedAt || tx.createdAt)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ────────────────────────────────────────────────
                      TAB 3: AFFILIATE & REFERRALS
                      ──────────────────────────────────────────────── */}
                  {activeTab === 'affiliate' && (
                    <div className="wallet-tab-content animate-fade-in">
                      {/* Top User Profile & Stats Row */}
                      <div className="affiliate-unified-profile glassmorphism">
                        <div className="affiliate-avatar-box">
                          {userDetails?.photos ? (
                            <img src={userDetails.photos} alt="Profile" className="affiliate-avatar-img" />
                          ) : (
                            <div className="affiliate-avatar-initials">
                              {getUserInitials(userDetails?.firstName, userDetails?.lastName)}
                            </div>
                          )}
                          <div>
                            <h2 className="affiliate-name">
                              {(userDetails?.firstName || "Member") + (userDetails?.lastName ? ` ${userDetails?.lastName}` : "")}
                            </h2>
                            <span className="affiliate-tag">Verified Amalgamate Affiliate</span>
                          </div>
                        </div>

                        <div className="affiliate-quick-stats">
                          <div className="affiliate-stat-pill">
                            <span>Total Affiliate Earnings</span>
                            <strong>{formatMoney(affiliateTotal)}</strong>
                          </div>
                          <div className="affiliate-stat-pill">
                            <span>Referred Subscribers</span>
                            <strong>{affiliateReferralsCount}</strong>
                          </div>
                        </div>
                      </div>

                      {/* Referral Sharing Links Box */}
                      <div className="affiliate-sharing-grid">
                        <div className="affiliate-box glassmorphism">
                          <h3>Your Referral Link</h3>
                          <p>Share this link with friends and followers. When they subscribe or join, commissions credit here.</p>
                          <div className="affiliate-copy-row">
                            <input
                              type="text"
                              readOnly
                              value={affiliateLink || "https://amalgamate.com/?ref=" + affiliateCode}
                              className="affiliate-input"
                            />
                            <button
                              className="affiliate-copy-btn"
                              onClick={() => handleCopy(affiliateLink || "https://amalgamate.com/?ref=" + affiliateCode, 'link')}
                            >
                              {copiedType === 'link' ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                              <span>{copiedType === 'link' ? 'Copied' : 'Copy Link'}</span>
                            </button>
                          </div>
                        </div>

                        <div className="affiliate-box glassmorphism">
                          <h3>Your Affiliate Code</h3>
                          <p>Users can also type your promo code manually during onboarding or checkout.</p>
                          <div className="affiliate-copy-row">
                            <input
                              type="text"
                              readOnly
                              value={affiliateCode}
                              className="affiliate-input"
                            />
                            <button
                              className="affiliate-copy-btn"
                              onClick={() => handleCopy(affiliateCode, 'code')}
                            >
                              {copiedType === 'code' ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                              <span>{copiedType === 'code' ? 'Copied' : 'Copy Code'}</span>
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Unified Direct Stripe Withdrawal Notice */}
                      <div className="affiliate-payout-note glassmorphism">
                        <div className="affiliate-payout-note__content">
                          <div>
                            <h4>Unified Stripe Connect Payouts</h4>
                            <p>
                              Your affiliate commissions are automatically aggregated into your Amalgamate Stripe wallet. There is no need to download an APK or submit separate withdrawal tickets—funds deposit into your connected bank account.
                            </p>
                          </div>
                          <button
                            className="payout-btn-primary"
                            onClick={handleLaunchDashboard}
                            disabled={isRedirecting}
                          >
                            <LaunchIcon style={{ fontSize: "16px", marginRight: "6px" }} />
                            Launch Stripe Payouts
                          </button>
                        </div>
                      </div>

                      {/* Referral Signups Audit List */}
                      <div className="ledger-section glassmorphism animate-fade-in" style={{ marginTop: '24px' }}>
                        <div className="ledger-header">
                          <h2 className="ledger-title">Referrals &amp; Commissions Breakdown</h2>
                          <span className="ledger-count">{referralData?.data?.transactions?.length || 0} referrals</span>
                        </div>

                        {(!referralData?.data?.transactions || referralData.data.transactions.length === 0) ? (
                          <div className="empty-ledger-state">
                            <PeopleAltIcon className="empty-icon" />
                            <p>No referrals yet. Share your link above to start earning passive commissions!</p>
                          </div>
                        ) : (
                          <div className="table-responsive">
                            <table className="ledger-table">
                              <thead>
                                <tr>
                                  <th>Subscriber / Member</th>
                                  <th>Plan / Action</th>
                                  <th>Commission Earned</th>
                                  <th>Date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {referralData.data.transactions.map((ref, idx) => (
                                  <tr key={idx} className="ledger-row">
                                    <td>
                                      <strong>{ref.userName || ref.userEmail || `Subscriber #${idx + 1}`}</strong>
                                    </td>
                                    <td>
                                      <span className="ledger-type-badge type-affiliate">
                                        {ref.type || "Subscription Commission"}
                                      </span>
                                    </td>
                                    <td>
                                      <strong style={{ color: "#28a745" }}>
                                        {formatMoney(ref.earning || ref.amount || 0)}
                                      </strong>
                                    </td>
                                    <td>{formatDate(ref.createdAt || ref.date)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ────────────────────────────────────────────────
                      TAB 4: PAYOUT SETTINGS (QUICK LAUNCH)
                      ──────────────────────────────────────────────── */}
                  {activeTab === 'payouts' && (
                    <div className="wallet-tab-content animate-fade-in">
                      <div className="payout-status-card">
                        <h2 style={{ color: '#fff', fontSize: '20px', marginBottom: '8px' }}>Stripe Payout Management</h2>
                        <p style={{ color: '#777', fontSize: '14px', marginBottom: '20px' }}>
                          Manage your bank accounts, tax details, and payout schedules directly on the official Stripe Connect Express portal.
                        </p>
                        <div style={{ display: 'flex', gap: '14px' }}>
                          <button className="payout-btn-primary" onClick={handleLaunchDashboard}>
                            <LaunchIcon style={{ fontSize: "18px", marginRight: "6px" }} />
                            Open Stripe Express Dashboard
                          </button>
                          <button className="payout-btn-secondary" onClick={() => navigate('/facilitate/settings/payouts')}>
                            <SettingsIcon style={{ fontSize: "18px", marginRight: "6px" }} />
                            Configure Payout Settings
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </main>
        </div>
        <ToastContainer position="top-right" autoClose={3000} />
      </div>
    </div>
  );
}
